import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPost } from "@/lib/apiAdmin";
import { HUD } from "./theme";

const DEFAULTS = ["www.reflectionsautodetail.com", "reflectionsautodetail.com"];

export const ApplePaySection = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [domain, setDomain] = useState("");
  const load = () => apiGet("/admin/apple-pay/domains").then(setD).catch(() => setD({ items: [] }));
  useEffect(() => { load(); }, []);
  const register = (dom) => {
    setBusy(dom);
    apiPost("/admin/apple-pay/register", { domain: dom })
      .then((r) => { r.status === "verified" ? toast.success(`${r.domain} verified for Apple Pay`) : toast.error(r.error || `Status: ${r.status}`); setDomain(""); load(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(""));
  };
  if (!d) return null;
  const known = new Set(d.items.map((x) => x.domain));
  const rows = [...d.items, ...DEFAULTS.filter((x) => !known.has(x)).map((x) => ({ domain: x, status: "not_registered" }))];
  return (
    <div className={`${HUD.panel} p-5 space-y-4`} data-testid="jarvis-applepay-section">
      <div>
        <h3 className="text-white font-['Outfit'] text-lg">Apple Pay</h3>
        <p className="text-xs text-gray-400 mt-1">Apple Pay shows on iPhones and Macs (Safari) only after the website domain is verified. Publish the site and connect your domain first, then press Verify. Google Pay needs no setup.</p>
        {d.processor === "nmi" && <p className="text-xs text-amber-300 mt-2" data-testid="jarvis-applepay-nmi-note">Wholesale Payments is on: Apple Pay must also be switched on in your Wholesale merchant portal (Settings → Apple Pay) for these domains.</p>}
      </div>
      {rows.map((r) => (
        <div key={r.domain} className="flex flex-wrap items-center gap-3 rounded-xl border border-cyan-400/10 p-3 text-sm" data-testid={`jarvis-applepay-${r.domain}`}>
          {r.status === "verified" ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <XCircle className="w-4 h-4 text-gray-500" />}
          <div className="flex-1 min-w-[200px]">
            <p className="text-white">{r.domain}</p>
            <p className="text-[11px] text-gray-500" data-testid={`jarvis-applepay-status-${r.domain}`}>{r.status === "verified" ? "Verified — Apple Pay live" : r.status === "not_registered" ? "Not verified yet" : r.error || r.status}</p>
          </div>
          <button onClick={() => register(r.domain)} disabled={!!busy} className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1`} data-testid={`jarvis-applepay-verify-${r.domain}`}>
            {busy === r.domain ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {r.status === "verified" ? "Re-check" : "Verify"}
          </button>
          {known.has(r.domain) && <button onClick={() => apiDelete(`/admin/apple-pay/domains/${r.domain}`).then(load)} className="text-xs text-gray-500 hover:text-rose-300">forget</button>}
        </div>
      ))}
      <div className="flex gap-2">
        <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="Another domain" className={`h-9 flex-1 rounded-xl px-3 text-xs border ${HUD.input}`} data-testid="jarvis-applepay-domain-input" />
        <button onClick={() => register(domain)} disabled={!domain.trim() || !!busy} className={`${HUD.btn} h-9 px-4 text-xs disabled:opacity-40`} data-testid="jarvis-applepay-add">Add & verify</button>
      </div>
    </div>
  );
};
